/**
 * Point-in-polygon (ray casting). Returns true if [lat, lng] is inside polygon (array of [lat, lng]).
 */
export function pointInPolygon(lat, lng, polygon) {
  if (!polygon || polygon.length < 3) return false;
  const x = lng;
  const y = lat;
  let inside = false;
  const n = polygon.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polygon[i][1];
    const yi = polygon[i][0];
    const xj = polygon[j][1];
    const yj = polygon[j][0];
    const intersect = yi > y !== yj > y && x < (xj - xi) * (y - yi) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** Default SLA: download (Mbps) below this = "below SLA" for speed view */
const DEFAULT_SPEED_SLA_MBPS = 10;
/** Signal (dBm) above this (e.g. -110) = "below SLA" for signal view (weaker signal) */
const DEFAULT_SIGNAL_SLA_DBM = -110;

/** WiFi signal % below this = below SLA */
const DEFAULT_WIFI_SLA_PCT = 20;

/**
 * Compute stats for points inside vs outside a polygon.
 * dataType: 'signal_strength' | 'internet_speed'
 * signalMetric: 'lte' | 'wifi' (only used when dataType === 'signal_strength')
 * Returns { inside: { count, avgDownload, avgUpload, medianSignal, pctBelowSla }, outside: same }
 */
export function computeZoneStats(points, polygon, dataType, signalMetric = 'lte') {
  if (!points || !polygon || polygon.length < 3) {
    return { inside: null, outside: null };
  }
  const insidePoints = [];
  const outsidePoints = [];
  for (const p of points) {
    const lat = p.lat != null ? Number(p.lat) : NaN;
    const lng = p.lng != null ? Number(p.lng) : NaN;
    if (Number.isNaN(lat) || Number.isNaN(lng)) continue;
    if (pointInPolygon(lat, lng, polygon)) insidePoints.push(p);
    else outsidePoints.push(p);
  }

  const toStats = (arr) => {
    if (arr.length === 0) return { count: 0, avgDownload: null, avgUpload: null, medianSignal: null, pctBelowSla: null };
    const withNum = (v) => (v != null && !Number.isNaN(Number(v))) ? Number(v) : null;
    let sumDown = 0, countDown = 0, sumUp = 0, countUp = 0;
    const signals = [];
    let belowSla = 0;
    for (const p of arr) {
      if (dataType === 'internet_speed') {
        const d = withNum(p.download_mbps);
        const u = withNum(p.upload_mbps);
        if (d != null) { sumDown += d; countDown++; }
        if (u != null) { sumUp += u; countUp++; }
        if (d != null && d < DEFAULT_SPEED_SLA_MBPS) belowSla++;
      } else {
        const s = withNum(p.value);
        if (s != null) signals.push(s);
        if (signalMetric === 'wifi') {
          if (s != null && s < DEFAULT_WIFI_SLA_PCT) belowSla++;
        } else {
          if (s != null && s < DEFAULT_SIGNAL_SLA_DBM) belowSla++;
        }
      }
    }
    const total = arr.length;
    const pctBelowSla = total > 0 ? Math.round((belowSla / total) * 100) : null;
    let medianSignal = null;
    if (signals.length > 0) {
      signals.sort((a, b) => a - b);
      const mid = Math.floor(signals.length / 2);
      medianSignal = signals.length % 2 ? signals[mid] : (signals[mid - 1] + signals[mid]) / 2;
    }
    return {
      count: total,
      avgDownload: countDown > 0 ? Math.round(sumDown / countDown * 10) / 10 : null,
      avgUpload: countUp > 0 ? Math.round(sumUp / countUp * 10) / 10 : null,
      medianSignal,
      pctBelowSla,
    };
  };

  return {
    inside: toStats(insidePoints),
    outside: toStats(outsidePoints),
  };
}

export { DEFAULT_SPEED_SLA_MBPS, DEFAULT_SIGNAL_SLA_DBM };
